import moment from 'moment'
import type { RefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { fetchFlightCalendar } from 'actions/FlightActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FlightsClassTypes } from 'constants/flight'
import { FlightsFareTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addMonths, endOfMonth, startOfMonth } from 'lib/datetime/dateUtils'
import { getFlightCalendarKey } from 'lib/flights/flightUtils'

interface Options {
  departureAirportCode?: string
  arrivalAirportCode?: string
  fareType?: FlightsFareTypes
  fareClass?: FlightsClassTypes
  /** Set on flight deal pages, where the calendar deliberately shows no fares. */
  disabled?: boolean
  /** `YYYY-MM` the calendar opens on, before anyone scrolls. */
  anchorMonth: string
  /**
   * `YYYY-MM` of the calendar's first rendered month. The mounted window is
   * clamped against it, so the request has to be too.
   */
  rangeStartMonth?: string
  /** The element the months scroll inside, so the fetch can follow the view. */
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export interface FlightCalendarPrices {
  /** Cached return fare by ISO date, in whole currency units. */
  cachedPriceFor: (isoDate: string) => number | undefined
  /** The lowest cached fare in a given `YYYY-MM`, for the "cheapest" marker. */
  isCheapestInMonth: (isoDate: string) => boolean
  loading: boolean
  /**
   * Whether fares should be shown. A search can carry cached fares it is not
   * allowed to display — flight deal pages set `disableCalendarPricing` — and
   * the cache is shared, so skipping the fetch is not enough on its own.
   */
  arePricesVisible: boolean
  earliestBookingDate?: string
  finalBookingDate?: string
}

/**
 * How many months the calendar keeps mounted: `renderAhead * 2 + 1`, three at
 * the default. The request has to span all of them, or a mounted, visible,
 * clickable month shows no fares and a click there records no quote.
 */
const MOUNTED_MONTH_COUNT = 3

/**
 * The window to fetch, mirroring how `useMonthWindow` places its mounted set.
 *
 * That window starts at `max(anchorIndex - renderAhead, 0)`, so it normally
 * reaches one month back from the reported month -- but at the very start of
 * the range the clamp bites and it extends forward instead. Deriving the
 * request the same way keeps the two in step at both ends: centring it
 * unconditionally would leave the last mounted month fareless whenever the
 * calendar opens on its first month, which is the state the drawer opens in
 * before a check-in is picked.
 */
export function toRequestWindow(month: string, rangeStartMonth?: string) {
  const isAtRangeStart = !!rangeStartMonth && month <= rangeStartMonth
  const monthStart = moment(month, 'YYYY-MM').toDate()
  const windowStart = isAtRangeStart ? monthStart : addMonths(monthStart, -1)

  return {
    startDate: moment(startOfMonth(windowStart)).format(ISO_DATE_FORMAT),
    endDate: moment(endOfMonth(addMonths(windowStart, MOUNTED_MONTH_COUNT - 1)))
      .format(ISO_DATE_FORMAT),
  }
}

/**
 * The per-day fares behind the flight calendar.
 *
 * Fares are cached per airport pair, cabin and month window, so the request
 * follows whichever month is actually on screen rather than being fetched once
 * for the month the calendar happened to open on. A scrolling calendar would
 * otherwise show prices for its first month and blanks for every month after.
 *
 * Multi-city and one-way legs are quoted per search rather than per day, which
 * is why neither asks for a calendar.
 */
/**
 * The month sitting at the top of a scrolled calendar.
 *
 * Reads one date per mounted month rather than walking every day cell: the
 * naive version measured hundreds of cells on every scroll frame, forcing a
 * synchronous layout each time.
 *
 * Measured against the scroller's own box, not with `offsetTop`. Each week is
 * a positioned row, so a day cell's offset parent is its week rather than the
 * scroller — its `offsetTop` is a couple of pixels no matter which month it
 * belongs to, and comparing that against `scrollTop` would name the last
 * mounted month every time.
 */
export function readTopmostMonth(container: HTMLElement): string | undefined {
  const containerTop = container.getBoundingClientRect().top
  let firstMountedMonth: string | undefined
  let topmostMonth: string | undefined

  for (const marker of container.querySelectorAll<HTMLElement>('[data-calendar-date$="-01"]')) {
    // The first of a month also appears as padding in the month before it.
    if (marker.classList.contains('adjacent')) continue

    const monthKey = marker.dataset.calendarDate?.slice(0, 7)
    firstMountedMonth = firstMountedMonth ?? monthKey
    if (marker.getBoundingClientRect().top > containerTop) break
    topmostMonth = monthKey
  }

  // Nothing has reached the top yet, so the window is still on its first month.
  return topmostMonth ?? firstMountedMonth
}

export default function useFlightCalendarPrices(options: Options): FlightCalendarPrices {
  const {
    departureAirportCode,
    arrivalAirportCode,
    fareType,
    fareClass,
    disabled = false,
    anchorMonth,
    rangeStartMonth,
    scrollContainerRef,
  } = options

  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const [visibleMonth, setVisibleMonth] = useState(anchorMonth)

  useEffect(() => setVisibleMonth(anchorMonth), [anchorMonth])

  /** Follows the topmost day on screen, so scrolling pulls in that month's fares. */
  useEffect(() => {
    const container = scrollContainerRef?.current
    if (!container) return

    const readVisibleMonth = () => {
      const month = readTopmostMonth(container)
      if (month) setVisibleMonth(month)
    }

    let frame = 0
    const onScroll = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(readVisibleMonth)
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.cancelAnimationFrame(frame)
      container.removeEventListener('scroll', onScroll)
    }
  }, [scrollContainerRef])

  const isPriceable = !disabled &&
    !!departureAirportCode &&
    !!arrivalAirportCode &&
    fareType !== FlightsFareTypes.MULTI_CITY &&
    fareType !== FlightsFareTypes.ONE_WAY

  const window_ = useMemo(
    () => toRequestWindow(visibleMonth, rangeStartMonth),
    [visibleMonth, rangeStartMonth],
  )
  const tripType = fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' as const : 'Return' as const

  const cacheKey = getFlightCalendarKey({
    startDate: window_.startDate,
    endDate: window_.endDate,
    origin: departureAirportCode ?? '',
    destination: arrivalAirportCode ?? '',
    region: currentRegionCode,
    currency: currentCurrency,
    tripType,
    fareClass,
  })

  const flightCalendar = useAppSelector(state => state.flights.flightCalendar[cacheKey])
  const loading = useAppSelector(state => !!state.flights.flightCalendarLoading[cacheKey])

  useEffect(() => {
    if (!isPriceable) return
    dispatch(fetchFlightCalendar({
      startDate: window_.startDate,
      endDate: window_.endDate,
      origin: departureAirportCode,
      destination: arrivalAirportCode,
      tripType,
      fareClass,
    }))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, isPriceable])

  const cachedPrices = flightCalendar?.cachedPrices

  const cheapestByMonth = useMemo(() => {
    const cheapest = new Map<string, number>()
    for (const [date, value] of Object.entries(cachedPrices ?? {})) {
      const month = date.slice(0, 7)
      const current = cheapest.get(month)
      if (current === undefined || value.cost < current) cheapest.set(month, value.cost)
    }
    return cheapest
  }, [cachedPrices])

  const cachedPriceFor = useCallback(
    (isoDate: string) => cachedPrices?.[isoDate]?.cost,
    [cachedPrices],
  )

  const isCheapestInMonth = useCallback((isoDate: string) => {
    const cost = cachedPrices?.[isoDate]?.cost
    return cost !== undefined && cheapestByMonth.get(isoDate.slice(0, 7)) === cost
  }, [cachedPrices, cheapestByMonth])

  /*
   * Held stable across renders: the calendar's `renderDay` and its day-click
   * handler both depend on this, so a fresh object each time would rebuild
   * them on every parent render and re-render every day cell on the grid.
   */
  return useMemo(() => ({
    cachedPriceFor,
    isCheapestInMonth,
    loading,
    arePricesVisible: !disabled,
    earliestBookingDate: flightCalendar?.earliestBookingDate,
    finalBookingDate: flightCalendar?.finalBookingDate,
  }), [
    cachedPriceFor,
    isCheapestInMonth,
    loading,
    disabled,
    flightCalendar?.earliestBookingDate,
    flightCalendar?.finalBookingDate,
  ])
}
